"use client";

import { useState } from "react";
import {
  DataTable,
  PillGroup,
  SelectField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  EmissionPanel,
  MillikanPanel,
} from "@/components/tools/physics-waves/photoelectric-panels";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { ELEMENTARY_CHARGE } from "@/lib/tool-kit/physics-waves/electromagnetic";
import { formatSig } from "@/lib/tool-kit/physics-waves/format";
import { METALS, threshold } from "@/lib/tool-kit/physics-waves/photoelectric";

/** English strings. A translated content file overrides these through `ui`. */
export const PHOTOELECTRIC_EFFECT_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to do?",
  modes: {
    emission: "Light on a metal",
    millikan: "Find h from stopping potentials",
  },
  sigFigs: "Round to this many significant figures",
  unitFor: "Unit for {name}",
  results: "Results",
  working: "The working",
  enterValue: "Enter a valid number above zero to see the result.",
  needPositive: "The photon and the work function must both be above zero.",
  photonLabel: "Describe the light by its",
  photonKinds: {
    wavelength: "Wavelength",
    frequency: "Frequency",
    energy: "Photon energy",
  },
  metalLabel: "Metal",
  metalOption: "{name} ({symbol}), φ = {phi} eV",
  customMetal: "Enter my own work function…",
  customLabel: "Work function φ (eV)",
  metals: {
    cs: "Cesium",
    rb: "Rubidium",
    k: "Potassium",
    na: "Sodium",
    ca: "Calcium",
    li: "Lithium",
    mg: "Magnesium",
    zn: "Zinc",
    ti: "Titanium",
    pb: "Lead",
    al: "Aluminium",
    ag: "Silver",
    w: "Tungsten",
    fe: "Iron",
    cu: "Copper",
    ni: "Nickel",
    au: "Gold",
    pt: "Platinum",
    be: "Beryllium",
  },
  kineticEnergy: "Maximum kinetic energy (KEmax)",
  stoppingPotential: "Stopping potential",
  kineticJoules: "KEmax in joules",
  maxSpeed: "Fastest electron speed",
  thresholdFrequency: "Threshold frequency",
  thresholdWavelength: "Threshold wavelength",
  belowThreshold:
    "Each photon carries {short} too little energy to free an electron, so no electrons are emitted however intense the light is.",
  relativisticNote:
    "The kinetic energy is above 10 keV, where the non-relativistic speed formula is only approximate.",
  stepPhoton: "Photon: f = {f}, so E = h × f = {e}",
  stepKinetic: "KEmax = E − φ = {e} − {phi} = {ke}",
  stepNone: "E − φ = {e} − {phi} is negative, so no electron escapes",
  stepThreshold: "Threshold: f₀ = φ ÷ h = {f0}, and λ₀ = hc ÷ φ = {l0}",
  barLabel: "Photon energy split into work function and kinetic energy",
  barDescription:
    "A bar for the photon's energy: the first part pays the work function, and what remains is the electron's maximum kinetic energy.",
  barPhi: "φ = {value}",
  barPhoton: "Photon energy {value}",
  barKinetic: "KEmax = {value}",
  graphLabel: "Maximum kinetic energy against frequency",
  graphDescription:
    "A straight line of slope h that crosses zero at the threshold frequency and meets the vertical axis at minus the work function, with a dot for the light you entered.",
  graphX: "Frequency (10¹⁴ Hz)",
  graphY: "Maximum kinetic energy (eV)",
  frequencyUnit: "Frequency unit",
  rowFrequency: "Frequency {n}",
  rowVoltage: "Stopping potential {n} (V)",
  removeRow: "Remove point {n}",
  addRow: "Add a point",
  loadSample: "Load sample data",
  millikanNeeds:
    "Enter at least two points with different frequencies whose stopping potential rises with frequency.",
  planckEstimate: "Planck constant from the slope",
  planckError: "Difference from the exact value",
  workFunctionResult: "Work function from the intercept",
  closestMetal: "Closest metal in the table",
  rSquared: "R² of the line",
  stepSlope: "Slope = {slope}, so h = e × slope = {h}",
  stepIntercept: "Intercept = {intercept}, so φ = −intercept × e = {phi}",
  exactNote: "Exact values for comparison",
  columnConstant: "Constant",
  columnValue: "Value",
  planckExact: "Planck constant h",
  hOverE: "h ÷ e",
  millikanLabel: "Stopping potential against frequency",
  millikanDescription:
    "Measured stopping potentials against frequency with the best-fit line; its slope is h divided by e.",
  millikanY: "Stopping potential (V)",
  tableHeading: "Work functions of common metals",
  columnMetal: "Metal",
  columnPhi: "φ (eV)",
  columnRange: "Handbook range (eV)",
  columnThreshold: "Threshold wavelength (nm)",
  tableNote:
    "Values from the CRC Handbook of Chemistry and Physics (2008), as tabulated in Wikipedia's Work function article. Where the handbook gives a range, which depends on the crystal face and surface condition, the lowest value is used in the calculator.",
} as const;

type Mode = keyof typeof PHOTOELECTRIC_EFFECT_CALCULATOR_STRINGS.modes;

const SIG_CHOICES = ["3", "4", "5"] as const;

export default function PhotoelectricEffectCalculatorWidget() {
  const strings = useToolUi(PHOTOELECTRIC_EFFECT_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("emission");
  const [sigText, setSigText] = useState<(typeof SIG_CHOICES)[number]>("4");
  const sig = Number(sigText);
  const rows = [...METALS]
    .sort((a, b) => a.phi - b.phi)
    .map((metal) => [
      `${strings.metals[metal.id]} (${metal.symbol})`,
      fmt(metal.phi, { maximumFractionDigits: 3, minimumFractionDigits: 2 }),
      metal.phiHigh === undefined
        ? "—"
        : `${fmt(metal.phi, { minimumFractionDigits: 2 })}–${fmt(metal.phiHigh, { minimumFractionDigits: 2 })}`,
      formatSig(
        fmt,
        threshold(metal.phi * ELEMENTARY_CHARGE).wavelength * 1e9,
        4
      ),
    ]);
  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="pe-mode"
        onChange={setMode}
        options={(Object.keys(strings.modes) as Mode[]).map((value) => ({
          value,
          label: strings.modes[value],
        }))}
        value={mode}
      />
      <div className="max-w-xs">
        <SelectField
          id="pe-sig"
          label={strings.sigFigs}
          onChange={setSigText}
          options={SIG_CHOICES.map((value) => ({ value, label: value }))}
          value={sigText}
        />
      </div>
      {mode === "emission" ? (
        <EmissionPanel fmt={fmt} sig={sig} strings={strings} />
      ) : null}
      {mode === "millikan" ? (
        <MillikanPanel fmt={fmt} sig={sig} strings={strings} />
      ) : null}
      <div className="space-y-3">
        <h2 className={WIDGET_H3}>{strings.tableHeading}</h2>
        <DataTable
          head={[
            strings.columnMetal,
            strings.columnPhi,
            strings.columnRange,
            strings.columnThreshold,
          ]}
          rows={rows}
        />
        <p className="text-muted-foreground text-xs">{strings.tableNote}</p>
      </div>
    </>
  );
}
